import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronLeft, Home, LayoutGrid, Search, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useVisibleNav } from "@/components/layout/navConfig";
import { buildMobileNav, findLocation } from "@/mobile/sections";
import useNotifications from "@/mobile/useNotifications";
import MobileHome from "@/mobile/MobileHome";
import { MobileModuleList, MobileSubmenu } from "@/mobile/MobileModuleList";
import MobileSearch from "@/mobile/MobileSearch";
import MobileAlerts from "@/mobile/MobileAlerts";
import MobileAccount from "@/mobile/MobileAccount";
import "@/mobile/mobile.css";

// Shell screens live at "/?m=<screen>" — "/" is reachable for every user, so
// the sidebar's route guard never interferes. "/" with no `m` is Home; the
// Dashboard page itself opens at "/?m=dashboard".
export default function MobileShell() {
  const { user } = useAuth();
  const vis = useVisibleNav(user, { mobileShell: true });
  const { modules, groups, sectionOf } = buildMobileNav(vis);
  const location = useLocation();
  const nav = useNavigate();
  const notes = useNotifications();

  const params = new URLSearchParams(location.search);
  const m = location.pathname === "/" ? params.get("m") : null;
  const isHome = location.pathname === "/" && !m;
  const screen = isHome ? "home" : ["modules", "search", "alerts", "account", "menu"].includes(m) ? m : "page";
  // A section opened from Home ("/?sec=") or from the Modules tab ("/?m=modules&sec=").
  const sec = screen === "home" || screen === "modules" ? params.get("sec") : null;
  const tab = screen === "page" || screen === "menu" ? "modules" : screen;

  const where = screen === "page" ? findLocation(modules, location.pathname) : null;
  // Sales Report is a full app in a frame: edge to edge, filling the space between the bars.
  const fullBleed = location.pathname === "/sales-report";
  const menuModule = screen === "menu" ? modules.find((x) => x.to === params.get("to")) : null;

  const goBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) nav(-1);
    else if (where?.child) nav(`/?m=menu&to=${encodeURIComponent(where.module.to)}`);
    else nav("/");
  };

  const searchIn = "all";
  const onSearch = (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    nav(`/?m=search&q=${encodeURIComponent(fd.get("q") || "")}`, { replace: screen === "search" });
  };

  const activeSectionKey = sec || (screen === "page" && where ? sectionOf(where.module.to).key : screen === "menu" && menuModule ? sectionOf(menuModule.to).key : null);

  return (
    <div className="m-root flex flex-col h-[100dvh] w-full overflow-hidden bg-[var(--m-bg)] text-[var(--m-text)]" data-testid="mobile-shell">
      {/* Top header */}
      <header className="shrink-0 m-safe-top text-white" style={{ background: "var(--m-header)" }} data-testid="mobile-header">
        <div className="flex items-center gap-2 px-3 py-2">
          <button type="button" onClick={() => nav("/")} className="shrink-0 h-11 flex items-center text-[19px] font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }} aria-label="Canares AI home">
            Canares<span style={{ color: "var(--m-accent)" }}>AI</span>
          </button>
          <form onSubmit={onSearch} className="flex-1 min-w-0 flex h-10 rounded-md overflow-hidden bg-white" role="search" data-testid="mobile-search-form">
            <input name="q" key={`${screen}-${params.get("q") || ""}`} defaultValue={screen === "search" ? params.get("q") || "" : ""}
              type="search" enterKeyHint="search" placeholder="Search modules & pages" aria-label="Search"
              className="flex-1 min-w-0 px-2 text-[15px] text-[#0f1111] placeholder:text-[#6b7280] focus:outline-none" data-testid="mobile-search-input" />
            <button type="submit" className="shrink-0 w-11 flex items-center justify-center" style={{ background: "var(--m-accent)" }} aria-label="Search">
              <Search className="w-5 h-5 text-[#0f1111]" />
            </button>
          </form>
        </div>
      </header>

      {/* Section links */}
      <nav className="shrink-0 overflow-x-auto m-no-scrollbar text-white" style={{ background: "var(--m-bar)" }} aria-label="Sections" data-testid="mobile-section-bar">
        <div className="flex px-1.5 w-max">
          {[{ key: "all", label: "All" }, ...groups].map((g) => {
            const active = g.key === "all" ? screen === "modules" && !sec : activeSectionKey === g.key;
            return (
              <button key={g.key} type="button"
                onClick={() => nav(g.key === "all" ? "/?m=modules" : screen === "modules" ? `/?m=modules&sec=${g.key}` : `/?sec=${g.key}`)}
                className="h-11 px-2.5 text-[13px] whitespace-nowrap border-b-2"
                style={{ borderColor: active ? "var(--m-accent)" : "transparent", color: active ? "#fff" : "#d5dbe1", fontWeight: active ? 700 : 500 }}>
                {g.short || g.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Content */}
      <main className={`flex-1 overflow-x-hidden ${fullBleed && screen === "page" ? "flex flex-col overflow-y-hidden" : "overflow-y-auto"} ${screen === "page" ? "m-page" : ""}`} data-testid="mobile-main">
        {screen === "home" && <MobileHome groups={groups} section={sec} />}
        {screen === "modules" && <MobileModuleList groups={groups} section={sec} />}
        {screen === "menu" && <MobileSubmenu module={menuModule} section={menuModule ? sectionOf(menuModule.to) : null} />}
        {screen === "search" && <MobileSearch groups={groups} query={params.get("q") || ""} section={searchIn} />}
        {screen === "alerts" && <MobileAlerts notes={notes} />}
        {screen === "account" && <MobileAccount user={user} />}
        {screen === "page" && (
          <>
            <div className="sticky top-0 z-20 flex items-center gap-1 px-1.5 h-11 bg-[#eaeded]/95 backdrop-blur border-b border-[#d5d9d9] text-[13px] text-[#0f1111]" data-testid="mobile-breadcrumb">
              <button type="button" onClick={goBack} className="shrink-0 w-10 h-10 -my-1 flex items-center justify-center rounded-full active:bg-black/5" aria-label="Back" data-testid="mobile-back">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <ol className="flex items-center gap-1 min-w-0 overflow-x-auto m-no-scrollbar whitespace-nowrap">
                <li><button type="button" onClick={() => nav("/")} className="text-[#007185]">Home</button></li>
                {where && (
                  <>
                    <li aria-hidden className="text-[#8d9096]">›</li>
                    <li><button type="button" onClick={() => nav(`/?sec=${sectionOf(where.module.to).key}`)} className="text-[#007185]">{sectionOf(where.module.to).label}</button></li>
                    <li aria-hidden className="text-[#8d9096]">›</li>
                    <li>
                      {where.child
                        ? <button type="button" onClick={() => nav(`/?m=menu&to=${encodeURIComponent(where.module.to)}`)} className="text-[#007185]">{where.module.label}</button>
                        : <span className="font-semibold">{where.module.label}</span>}
                    </li>
                    {where.child && (<><li aria-hidden className="text-[#8d9096]">›</li><li className="font-semibold">{where.child.label}</li></>)}
                  </>
                )}
              </ol>
            </div>
            <div className={fullBleed ? "m-content flex-1 min-h-0 flex flex-col" : "m-content px-4 pt-3 pb-6"}>
              <Outlet />
            </div>
          </>
        )}
      </main>

      {/* Bottom tabs */}
      <nav className="shrink-0 m-safe-bottom border-t border-white/10" style={{ background: "var(--m-header)" }} aria-label="Main" data-testid="mobile-tabbar">
        <div className="grid grid-cols-5">
          {[
            { key: "home", label: "Home", icon: Home, to: "/" },
            { key: "modules", label: "Modules", icon: LayoutGrid, to: "/?m=modules" },
            { key: "search", label: "Search", icon: Search, to: "/?m=search" },
            { key: "alerts", label: "Alerts", icon: Bell, to: "/?m=alerts", badge: notes.unreadCount },
            { key: "account", label: "Account", icon: User, to: "/?m=account" },
          ].map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} type="button" onClick={() => nav(t.to)} aria-current={active ? "page" : undefined}
                className="relative h-14 flex flex-col items-center justify-center gap-0.5 text-[11px]"
                style={{ color: active ? "var(--m-accent)" : "#c7ced6", fontWeight: active ? 700 : 500 }} data-testid={`mobile-tab-${t.key}`}>
                {active && <span className="absolute top-0 inset-x-5 h-0.5 rounded-b" style={{ background: "var(--m-accent)" }} />}
                <span className="relative">
                  <t.icon className="w-[22px] h-[22px]" strokeWidth={active ? 2.4 : 2} />
                  {t.badge > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-4 text-center text-[#0f1111]" style={{ background: "var(--m-accent)" }}>
                      {t.badge > 99 ? "99+" : t.badge}
                    </span>
                  )}
                </span>
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
